import axios from 'axios';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import { usePermissions } from '@/lib/PermissionsContext';
import { GoogleMapsBtn } from '@/components/GoogleMapsBtn';
import { API_URL } from './utils';
import { Plus, ClipboardList, Home, TreePine, Trash2, Package, User, MapPin, ChevronDown, ChevronUp, Upload, Image as ImageIcon, CheckCircle, X, Calendar, ChevronLeft, ChevronRight, Clock, LogIn, LogOut as LogOutIcon, Edit, Edit3, Phone, Mail, Building2, Printer, ShoppingCart, History, Send, Wrench, AlertTriangle, AlertCircle, Users, Play, Square, Coffee, Check, MoreVertical, RefreshCw, Cloud, CheckCircle2, MessageSquare, FileText, UploadCloud, Download } from 'lucide-react';
import { haversineDistance } from './utils';

// 🗺️ [v13.63 Phase 3] Backend central /api/geo/geocode (Google → Nominatim → cache 24h)
async function geocodeAddress(adresse) {
  try {
    const r = await axios.get(`${API_URL}/api/geo/geocode`, { params: { adresse } });
    if (r?.data?.lat != null && r?.data?.lng != null) {
      return { lat: r.data.lat, lon: r.data.lng };
    }
  } catch { /* dégradation silencieuse */ }
  return null;
}

export function SectionInspection({ bon, canEdit = true }) {
  const { user } = useAuth();
  const { canDo } = usePermissions() || {};
  const [criteres, setCriteres] = useState([]);
  const [typeInspection, setTypeInspection] = useState(''); // interieur / exterieur_ferme / exterieur_ouvert
  const [reponses, setReponses] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadingCriteres, setLoadingCriteres] = useState(true);
  // Géolocalisation
  const [geoStatus, setGeoStatus] = useState('idle'); // 'idle' | 'checking' | 'ok' | 'blocked' | 'error'
  const [geoDistance, setGeoDistance] = useState(null);

  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC.
  //   - `canEdit` (prop) reste driver principal (branché sur `bt_inspection` en amont).
  //   - `inspection_criteres_gestion_masques` : masque les critères "gestion" (communication client, stationnement).
  //     Inverse la sémantique historique : quand True → l'user voit TOUS les critères (comportement admin/CP).
  //     Quand False → filtrage des critères "gestion" (comportement peintre).
  //   - `inspection_bypass_geolocation` : bypass la vérification GPS <250m du chantier.
  const isTerrainReadOnly     = !canEdit;
  const voitCriteresGestion   = !!(canDo && canDo('inspection_criteres_gestion_masques'));
  const bypassGeolocalisation = !!(canDo && canDo('inspection_bypass_geolocation'));
  const GEO_MAX_METERS = 250;

  // Détermine les types d'inspection disponibles selon le bon
  const typesDisponibles = bon.type_travaux === 'interieur'
    ? ['interieur']
    : ['exterieur_ferme', 'exterieur_ouvert'];

  const typeLabels = {
    interieur: 'Intérieur',
    exterieur_ferme: 'Ext. Fermé',
    exterieur_ouvert: 'Ext. Ouvert',
  };

  useEffect(() => {
    const defaultType = bon.type_travaux === 'interieur' ? 'interieur' : 'exterieur_ferme';
    setTypeInspection(defaultType);
    loadInspection(defaultType);
  }, [bon.id]);

  useEffect(() => {
    if (typeInspection) {
      fetchCriteres(typeInspection);
    }
  }, [typeInspection]);

  const fetchCriteres = async (type) => {
    setLoadingCriteres(true);
    try {
      const res = await axios.get(`${API_URL}/api/inspections/criteres/${type}`);
      if (res.status === 200) {
        const data = res.data;
        let allCriteres = data.criteres || [];
        // Filtrer les critères pour les peintres (ils ne voient pas les critères de gestion)
        // 🔀 Inversion sémantique : voitCriteresGestion=True → tous les critères ;
        //   False → filtrage des critères "gestion" (comportement historique peintre).
        if (!voitCriteresGestion) {
          if (type === 'interieur') {
            allCriteres = allCriteres.filter((_, i) => i > 0); // skip #1 "La communication..."
          } else if (type === 'exterieur_ouvert') {
            allCriteres = allCriteres.filter((_, i) => i > 1); // skip #1 "La communication..." et #2 "L'unité est stationnée..."
          }
          // exterieur_ferme : 100% des critères pour les peintres
        }
        setCriteres(allCriteres);
      }
    } catch (e) { /* ignore */ }
    finally { setLoadingCriteres(false); }
  };

  const loadInspection = async (defaultType) => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/bons-travail/${bon.id}/inspection`);
      if (res.status === 200) {
        const data = res.data;
        if (data.inspection?.reponses) {
          setReponses(data.inspection.reponses);
          if (data.inspection.type_inspection) {
            setTypeInspection(data.inspection.type_inspection);
          } else {
            setTypeInspection(defaultType);
          }
          setLoaded(true);
        }
      }
    } catch (e) { /* ignore */ }
  };

  const checkGeolocation = async () => {
    // Admins/CP/Proprietaires exempts de la vérification
    if (bypassGeolocalisation) {
      setGeoStatus('ok');
      return true;
    }
    setGeoStatus('checking');
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setGeoStatus('error');
        toast.error("Géolocalisation non supportée par ce navigateur");
        resolve(false);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const userLat = pos.coords.latitude;
          const userLon = pos.coords.longitude;
          // Récupérer les coordonnées du chantier
          let siteLat = bon.latitude_travaux;
          let siteLon = bon.longitude_travaux;
          if (!siteLat || !siteLon) {
            const addr = `${bon.adresse_travaux || ''}, ${bon.ville_travaux || ''}, Québec, Canada`;
            const coords = await geocodeAddress(addr);
            if (coords) { siteLat = coords.lat; siteLon = coords.lon; }
          }
          if (!siteLat || !siteLon) {
            // Impossible de géocoder, on laisse passer avec avertissement
            toast.warning("Adresse du chantier introuvable — vérification de proximité ignorée");
            setGeoStatus('ok');
            resolve(true);
            return;
          }
          const dist = haversineDistance(userLat, userLon, siteLat, siteLon);
          setGeoDistance(Math.round(dist));
          if (dist <= GEO_MAX_METERS) {
            setGeoStatus('ok');
            resolve(true);
          } else {
            setGeoStatus('blocked');
            resolve(false);
          }
        },
        (err) => {
          setGeoStatus('error');
          toast.error("Impossible d'obtenir votre position GPS. Activez la géolocalisation.");
          resolve(false);
        },
        { timeout: 10000, maximumAge: 30000 }
      );
    });
  };

  const handleExpand = async () => {
    if (!expanded) {
      // Les employés terrain (lecture seule) peuvent voir l'inspection sans vérification géo
      if (!isTerrainReadOnly) {
        const ok = await checkGeolocation();
        if (!ok && !bypassGeolocalisation) return;
      }
    }
    setExpanded(!expanded);
  };

  const toggleReponse = (id) => {
    setReponses(prev => {
      if (prev[id] === undefined) return { ...prev, [id]: true };
      if (prev[id] === true) return { ...prev, [id]: false };
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const nbTotal = criteres.length;
  const nbReussi = criteres.filter(c => reponses[c.id] === true).length;
  const nbEchoue = criteres.filter(c => reponses[c.id] === false).length;
  const reussie = nbEchoue === 0 && nbTotal > 0 && Object.keys(reponses).length === nbTotal;
  const pct = nbTotal > 0 ? Math.round((nbReussi / nbTotal) * 100) : 0;

  const handleSave = async () => {
    if (isTerrainReadOnly) return; // Terrain employees cannot save
    // Re-vérifier la géolocalisation avant de sauvegarder
    if (!bypassGeolocalisation) {
      const ok = await checkGeolocation();
      if (!ok) {
        toast.error(`Vous devez être dans un rayon de ${GEO_MAX_METERS}m du chantier pour sauvegarder l'inspection.`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/inspection`, {
          reponses,
          type_inspection: typeInspection,
          nb_total: nbTotal,
          nb_reussi: nbReussi,
          nb_echoue: nbEchoue,
          overall_percentage: pct,
          passed: reussie,
          inspected_by: user ? `${user.prenom} ${user.nom}` : 'Inconnu',
          inspecteur_nom: user ? `${user.prenom} ${user.nom}` : 'Inconnu',
        });
      if (res.status !== 200) throw new Error('Erreur');
      const data = res.data;
      if (data.reussie) {
        toast.success('Inspection réussie et sauvegardée!');
      } else {
        toast.warning(`Inspection sauvegardée — ${nbEchoue} critère(s) échoué(s)`);
      }
      setLoaded(true);
    } catch (e) { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-purple-200">
      <CardHeader>
        <div className="flex justify-between items-center cursor-pointer" onClick={handleExpand}>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-purple-600" />
            Inspection
            {isTerrainReadOnly && (
              <Badge className="bg-gray-100 text-gray-500 text-[10px]">Lecture seule</Badge>
            )}
            {loaded && (
              <Badge className={reussie ? 'bg-green-100 text-green-800' : nbEchoue > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}>
                {reussie ? '✓ Réussie' : nbEchoue > 0 ? `✗ ${nbEchoue} échoué(s)` : `${pct}% (${Object.keys(reponses).length}/${nbTotal})`}
              </Badge>
            )}
            {!isTerrainReadOnly && geoStatus === 'checking' && <span className="text-xs text-blue-500 animate-pulse">Vérification position...</span>}
            {!isTerrainReadOnly && geoStatus === 'ok' && geoDistance !== null && <span className="text-xs text-green-600">📍 {geoDistance}m du chantier</span>}
          </CardTitle>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </CardHeader>

      {/* Message bloqué si hors périmètre (admins/CP seulement) */}
      {!isTerrainReadOnly && geoStatus === 'blocked' && (
        <CardContent className="pt-0">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <MapPin className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-700">Inspection bloquée — Vous êtes trop loin du chantier</p>
              <p className="text-xs text-red-600 mt-1">
                Vous êtes à <strong>{geoDistance}m</strong> du chantier. L'inspection doit être remplie dans un rayon de {GEO_MAX_METERS}m.
              </p>
              <p className="text-xs text-red-500 mt-1">Rendez-vous sur place pour ouvrir l'inspection.</p>
            </div>
          </div>
        </CardContent>
      )}

      {expanded && geoStatus !== 'blocked' && (
        <CardContent>
          {/* Selector type (extérieur only) */}
          {bon.type_travaux === 'exterieur' && (
            <div className="flex gap-2 mb-4 p-2 bg-gray-50 rounded-lg">
              <span className="text-xs text-gray-500 flex items-center mr-2">Type de chantier :</span>
              {typesDisponibles.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setTypeInspection(t); setReponses({}); }}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all border ${
                    typeInspection === t
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'
                  }`}
                >
                  {typeLabels[t]}
                </button>
              ))}
            </div>
          )}

          {/* Inspector info — seulement pour les fillers */}
          {user && !isTerrainReadOnly && (
            <div className="text-xs text-gray-400 mb-3 flex items-center gap-1">
              <User className="w-3 h-3" />
              Inspection par : <strong>{user.prenom} {user.nom}</strong>
            </div>
          )}
          {isTerrainReadOnly && (
            <div className="text-xs text-amber-600 mb-3 flex items-center gap-1 bg-amber-50 rounded-lg p-2 border border-amber-200">
              <ClipboardList className="w-3 h-3 shrink-0" />
              <span>L'inspection est remplie par la chargée de projet. Vous consultez les résultats.</span>
            </div>
          )}

          {loadingCriteres ? (
            <p className="text-center text-gray-400 py-4 text-sm">Chargement des critères...</p>
          ) : (
            <div className="space-y-1.5">
              {criteres.map((c, idx) => {
                const val = reponses[c.id];
                return (
                  <div
                    key={c.id}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isTerrainReadOnly ? 'cursor-default' : 'cursor-pointer'
                    } ${
                      val === true ? 'bg-green-50' : val === false ? 'bg-red-50' : isTerrainReadOnly ? '' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => !isTerrainReadOnly && toggleReponse(c.id)}
                  >
                    <button
                      type="button"
                      disabled={isTerrainReadOnly}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                        val === true ? 'bg-green-500 border-green-500 text-white' :
                        val === false ? 'bg-red-500 border-red-500 text-white' :
                        'border-gray-300'
                      } ${isTerrainReadOnly ? 'opacity-60' : ''}`}
                      data-testid={`inspection-${idx}`}
                    >
                      {val === true && <CheckCircle className="w-3.5 h-3.5" />}
                      {val === false && <X className="w-3.5 h-3.5" />}
                    </button>
                    <span className="text-xs text-gray-400 w-5 font-mono shrink-0">{idx + 1}.</span>
                    <span className={val === false ? 'text-red-700' : ''}>{c.label}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-between items-center mt-4 pt-3 border-t">
            <div className="text-xs text-gray-500">
              {Object.keys(reponses).length}/{nbTotal} répondu(s) — {nbReussi} OK, {nbEchoue} NON
              {nbEchoue > 0 && <span className="text-red-500 ml-2">· Inspection échouée</span>}
            </div>
            {!isTerrainReadOnly && (
              <Button onClick={handleSave} disabled={saving} size="sm" className="bg-purple-600 hover:bg-purple-700" data-testid="btn-save-inspection">
                {saving ? 'Sauvegarde...' : 'Sauvegarder l\'inspection'}
              </Button>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}


/* ─── Section Gestion du Contrat (formulaire CP) ─── */
